import type {
  CatalogoPermissoesDto,
  PermissionRisk,
  SecretariaDraft,
  SecretariaMember,
  SecretariaPermissionGroup,
  SensibilidadePermissaoDto,
  UsuarioEquipeInternaDto,
} from '../types/secretariaPermissions';

export function normalizePermissionSearch(value: string) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR')
    .trim();
}
export function filterPermissionGroups(
  groups: SecretariaPermissionGroup[],
  search: string,
  risk: PermissionRisk | 'all',
) {
  const term = normalizePermissionSearch(search);
  return groups
    .map((group) => ({
      ...group,
      permissions: group.permissions.filter(
        (permission) =>
          (risk === 'all' || permission.risk === risk) &&
          (normalizePermissionSearch(group.label).includes(term) ||
            normalizePermissionSearch(permission.label).includes(term)),
      ),
    }))
    .filter((group) => group.permissions.length > 0);
}
export function togglePermissionSelection(selected: string[], ids: string[], checked?: boolean) {
  const next = new Set(selected);
  const shouldSelect = checked ?? !ids.every((id) => next.has(id));
  ids.forEach((id) => {
    if (shouldSelect) next.add(id);
    else next.delete(id);
  });
  return [...next];
}
export function hasSecretariaChanges(original: SecretariaDraft, draft: SecretariaDraft) {
  return (
    original.name !== draft.name ||
    original.email !== draft.email ||
    [...original.permissions].sort().join('|') !== [...draft.permissions].sort().join('|')
  );
}
const riscoPorSensibilidade: Record<SensibilidadePermissaoDto, PermissionRisk> = {
  operacional: 'success',
  leitura_sensivel: 'info',
  efeito_externo: 'warning',
  irreversivel: 'danger',
};

/** O catálogo do backend vira os grupos do checklist; a chave é o id que o PUT devolve ao backend. */
export function mapCatalogoToGroups(catalogo: CatalogoPermissoesDto): SecretariaPermissionGroup[] {
  return catalogo.grupos.map((grupo) => ({
    id: grupo.id,
    label: grupo.rotulo,
    area: grupo.area,
    permissions: grupo.permissoes.map((permissao) => ({
      id: permissao.chave,
      label: permissao.rotulo,
      risk: permissao.sensibilidade ? riscoPorSensibilidade[permissao.sensibilidade] : null,
    })),
  }));
}

/**
 * Data local `AAAA-MM-DD` de um instante ISO: a coluna "Data de criação" formata essa string, e
 * cortar o ISO em UTC mostraria o dia seguinte para quem foi criado depois das 21h em Brasília.
 */
function toLocalDateOnly(isoInstant: string): string {
  const date = new Date(isoInstant);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** As permissões ficam vazias na lista: o checklist as carrega ao abrir a pessoa. */
export function mapUsuarioToMember(usuario: UsuarioEquipeInternaDto): SecretariaMember {
  return {
    id: usuario.id,
    name: usuario.nomeCompleto,
    email: usuario.email,
    createdAt: toLocalDateOnly(usuario.dataCriacao),
    status: 'active',
    role: 'SECRETARIA',
    isAdmin: usuario.adminSecretaria,
    permissions: [],
  };
}
